/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

import { AxiosInstance } from "axios"
import { getAcceptLanguageCode } from "utils/i18n"
import { http401UnauthorizedHandler } from "utils/network"
import { CSRF_HEADER_NAME, getCsrfHeader } from "utils/csrf"

/*
  Instead of put axios interceptors inside store.ts it's better to put the interceptor here.
  Both of requestInterceptor and responseInterceptor function makes the code being testable
  inside store.ts without break the original mechanism

  Both of function below can be tested too
*/
export const requestInterceptor = (axiosClient: AxiosInstance) => {
  axiosClient.interceptors.request.use(
    config => {
      const modifiedConfig = { ...config }
      const extraHeaders: Record<string, string> = {
        "Accept-Language": getAcceptLanguageCode(false),
      }
      // State-changing requests mirror the auth token's jti into X-CSRF-Token:
      // the jti cookie under cookie auth, or the JWT's `jti` claim otherwise
      // (the injected-session WebView flow), so both transports send the same
      // header. getCsrfHeader owns that decision, and a cookie/JWT-derived value
      // always wins over any caller-supplied header.
      const csrfHeader = getCsrfHeader(config.method)
      if (csrfHeader[CSRF_HEADER_NAME]) {
        extraHeaders[CSRF_HEADER_NAME] = csrfHeader[CSRF_HEADER_NAME]
      }
      modifiedConfig.headers = config.headers?.concat(extraHeaders)

      return modifiedConfig
    },
    error => {
      return Promise.reject(error)
    },
  )
}

export const responseInterceptor = (
  axiosClient: AxiosInstance,
  isDev: boolean,
) => {
  axiosClient.interceptors.response.use(
    response => response,
    error => {
      if (error.response?.status === 401 && !isDev) {
        http401UnauthorizedHandler()
      }
      return Promise.reject(error)
    },
  )
}
